import React, {
  createContext, Dispatch, SetStateAction, useContext, useMemo, useState, useEffect, useCallback,
} from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import items from '../pages/Analysis/MenuItems';

interface AnalysisMenuInterface {
  selectedItem: typeof items[number],
  setSelectedItem: Dispatch<SetStateAction<typeof items[number]>>,
  isShowingSideMenu: boolean,
  setIsShowingSideMenu: Dispatch<SetStateAction<boolean>>,
  selectedItemIndex: number,
  navigateToItem: (index: number) => void,
  updateUrlParams: (params: { farm?: string, year?: string, fromMonth?: string, toMonth?: string }) => void,
}

const AnalysisContext = createContext<AnalysisMenuInterface>({} as AnalysisMenuInterface);
AnalysisContext.displayName = 'AnalysisContext';

interface Props {
  children: JSX.Element,
}

export const AnalysisMenuProvider: React.FC<Props> = ({ children }) => {
  const { itemId } = useParams<{ itemId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Find item by position string
  const findItemByPosition = (position: string): number => {
    const index = items.findIndex(item => item.position === position);
    return index >= 0 ? index : 1; // Default to index 1 if not found
  };

  // Initialize with localStorage or default value (using position from URL)
  const getInitialIndex = () => {
    if (itemId) {
      const index = findItemByPosition(itemId);
      if (index >= 0) {
        return index;
      }
    }
    const saved = localStorage.getItem('analysis_selectedItemIndex');
    if (saved) {
      const arrayIndex = parseInt(saved, 10);
      if (!isNaN(arrayIndex) && arrayIndex >= 0 && arrayIndex < items.length) {
        return arrayIndex;
      }
    }
    return 1; // Default to array index 1 (first real item)
  };

  const [selectedItemIndex, setSelectedItemIndex] = useState(getInitialIndex);
  const [selectedItem, setSelectedItem] = useState(items[selectedItemIndex] as typeof items[number]);
  const [isShowingSideMenu, setIsShowingSideMenu] = useState(true);

  // Sync selected index with URL when itemId changes (itemId is position string)
  useEffect(() => {
    if (itemId) {
      const arrayIndex = findItemByPosition(itemId);
      if (arrayIndex >= 0 && arrayIndex !== selectedItemIndex) {
        setSelectedItemIndex(arrayIndex);
        setSelectedItem(items[arrayIndex]);
      }
    }
  }, [itemId]);

  // Update selected item when index changes
  useEffect(() => {
    setSelectedItem(items[selectedItemIndex]);
    localStorage.setItem('analysis_selectedItemIndex', String(selectedItemIndex));
  }, [selectedItemIndex]);

  // Validate and correct fromMonth/toMonth from URL on initial load
  useEffect(() => {
    const urlFromMonth = searchParams.get('fromMonth');
    const urlToMonth = searchParams.get('toMonth');
    const urlYear = searchParams.get('year');
    const urlToYear = searchParams.get('toYear');

    if (urlFromMonth && urlToMonth) {
      const fromMonthNum = Number(urlFromMonth);
      const toMonthNum = Number(urlToMonth);
      const yearNum = Number(urlYear);
      const toYearNum = Number(urlToYear || urlYear);

      // Only correct if same year and toMonth < fromMonth
      // Cross-year ranges (e.g., Dec 2024 to Jan 2025) are valid
      const isSameYear = yearNum === toYearNum;
      if (isSameYear && toMonthNum < fromMonthNum && itemId) {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('toMonth', urlFromMonth);
        const path = `/analysis/${itemId}`;
        navigate(`${path}?${newParams.toString()}`, { replace: true });
      }
    }
  }, []);

  // Navigate to analysis item by array index
  const navigateToItem = useCallback((arrayIndex: number) => {
    if (arrayIndex >= 0 && arrayIndex < items.length) {
      const item = items[arrayIndex];
      const position = item.position || String(arrayIndex);
      const currentParams = new URLSearchParams(searchParams);
      navigate(`/analysis/${position}?${currentParams.toString()}`);
      setSelectedItemIndex(arrayIndex);
    }
  }, [navigate, searchParams]);

  // Update URL query parameters
  // Using useRef to always access the latest selectedItemIndex value
  const selectedItemIndexRef = React.useRef(selectedItemIndex);
  const itemIdRef = React.useRef(itemId);

  // Update refs when values change
  React.useEffect(() => {
    selectedItemIndexRef.current = selectedItemIndex;
  }, [selectedItemIndex]);

  React.useEffect(() => {
    itemIdRef.current = itemId;
  }, [itemId]);

  const updateUrlParams = useCallback((params: { farm?: string, year?: string, fromMonth?: string, toMonth?: string }) => {
    const newParams = new URLSearchParams(searchParams);

    if (params.farm !== undefined) newParams.set('farm', params.farm);
    if (params.year !== undefined) newParams.set('year', params.year);
    if (params.fromMonth !== undefined) newParams.set('fromMonth', params.fromMonth);
    if (params.toMonth !== undefined) newParams.set('toMonth', params.toMonth);

    // Use refs to get the current values, not stale closure values
    const currentSelectedIndex = selectedItemIndexRef.current;
    const currentItemId = itemIdRef.current;
    const currentItem = items[currentSelectedIndex];
    let position = currentItem?.position;

    // Only fallback to itemId if position is not found (edge case)
    if (!position) {
      position = currentItemId || '1';
    }

    const path = `/analysis/${position}`;
    navigate(`${path}?${newParams.toString()}`, { replace: true });
  }, [navigate, searchParams]);

  const value = useMemo(
    () => ({
      isShowingSideMenu,
      setIsShowingSideMenu,
      selectedItem,
      setSelectedItem,
      selectedItemIndex,
      navigateToItem,
      updateUrlParams,
    }),
    [selectedItem, isShowingSideMenu, selectedItemIndex, navigateToItem, updateUrlParams],
  );

  return (
    <AnalysisContext.Provider
      value={value}
    >
      {children}
    </AnalysisContext.Provider>
  );
};

export const useAnalysisMenuContext = (): AnalysisMenuInterface => {
  const context = useContext(AnalysisContext);

  return context;
};
